import type { MatchResolverContext, ResolverPlayer } from 'modules/manager/domain/placeholder';
import { BuiltInVideoSetId } from 'modules/match-video/domain/constants';
import { ReviewClip, ReviewFrameProvider, ReviewGoal, ReviewScene, VideoReviewOptions } from 'modules/match-video/domain/interfaces';
import {
  browserSceneEnv,
  createNightDangerPlate,
  createNightGoalPlate,
  createNightStadiumIntro,
  createSportSlashDangerPlate,
  createSportSlashGoalPlate,
  createSportSlashIntro,
  DangerPlateData,
  GoalPlateData,
  IntroSceneData,
  SceneEnv,
} from 'modules/match-video/domain/scenes';
import { toDangerPlateData, toGoalPlateData, toIntroSceneData } from './scene-data-from-match';

interface BuiltInSceneFactories {
  intro(data: IntroSceneData, env: SceneEnv): Promise<ReviewScene>;
  goal(data: GoalPlateData, env: SceneEnv): Promise<ReviewScene>;
  danger(data: DangerPlateData, env: SceneEnv): Promise<ReviewScene>;
}

const FACTORIES: Record<BuiltInVideoSetId, BuiltInSceneFactories> = {
  'night-stadium': { intro: createNightStadiumIntro, goal: createNightGoalPlate, danger: createNightDangerPlate },
  'sport-slash': { intro: createSportSlashIntro, goal: createSportSlashGoalPlate, danger: createSportSlashDangerPlate },
};

/** What a built-in set draws a review from: the match, not templates. */
export interface BuiltInReviewDesign {
  setId: BuiltInVideoSetId;
  match: MatchResolverContext;
  players: ReadonlyMap<number, ResolverPlayer>;
  goalByClipId: Readonly<Record<string, ReviewGoal>>;
  options: VideoReviewOptions;
  /** Interface language of the dates on the intro; none — the active one. */
  locale?: string;
}

/**
 * The intro and the plates of a built-in set as animated scenes, each built on demand (`ReviewFrameProvider`). A
 * built-in set always has both plates; a clip of another kind goes without one.
 */
export function createBuiltInFrameProvider(design: BuiltInReviewDesign, env: SceneEnv = browserSceneEnv): ReviewFrameProvider {
  const factories = FACTORIES[design.setId];
  return {
    intro: () => factories.intro(toIntroSceneData(design.match, design.locale), env),
    plate: async (clip: ReviewClip) => {
      if (design.options.plates === 'none') return null;
      if (clip.kind === 'goal')
        return factories.goal(toGoalPlateData(design.match, design.goalByClipId[clip.id], design.players), env);
      if (clip.kind === 'danger') return factories.danger(toDangerPlateData(design.match), env);
      return null;
    },
  };
}
